import { isEmpty, set } from "lodash";
import { queryClient } from "src/lib/react-query";
import type { SyncsOrderBy } from "src/graphql";
import {
  IntervalBinType,
  useDraftsQuery,
  useListFoldersQuery,
  useSyncConfigurationQuery,
  useSyncFilterOptionsQuery,
  useSyncQuery,
  useSyncRowsQueriedQuery,
  useSyncRunMetricsQuery,
  useSyncRunsQuery,
  useSyncsLabelsQuery,
  useSyncsQuery,
  useViewsQuery,
} from "src/graphql";
import {
  ChartGranularity,
  getChartTimeRange,
} from "src/components/charts/chart-utils";
import {
  type SortDirection,
  DEFAULT_SORT_DIRECTION,
  DEFAULT_TABLE_LIMIT,
} from "src/ui/table/use-table-config";
import { DEFAULT_SYNC_SORT_KEY, SYNC_SORT_OPTIONS } from "./constants";
import {
  buildSegmentHasuraFilters,
  buildSyncHasuraFilters,
  parseSyncFilterParams,
} from "./filters";

const getSyncsQueryVariables = (searchParams: URLSearchParams) => {
  const sortKeyParam = searchParams.get("sortKey");
  const sortKey =
    sortKeyParam && (SYNC_SORT_OPTIONS as string[]).includes(sortKeyParam)
      ? sortKeyParam
      : DEFAULT_SYNC_SORT_KEY;

  const sortDirParam = searchParams.get("sortDirection");
  const sortDirection: SortDirection =
    sortDirParam === "asc" || sortDirParam === "desc"
      ? sortDirParam
      : DEFAULT_SORT_DIRECTION;

  const filterParams = parseSyncFilterParams(searchParams);
  const syncFilters = buildSyncHasuraFilters(filterParams);
  const segmentFilters = buildSegmentHasuraFilters(filterParams);

  return {
    offset: 0,
    limit: DEFAULT_TABLE_LIMIT,
    filters: {
      ...syncFilters,
      ...(isEmpty(segmentFilters) ? {} : { segment: segmentFilters }),
    },
    orderBy: set<SyncsOrderBy>({}, sortKey, sortDirection),
  };
};

/**
 * Fire-and-forget loader for /syncs.
 * Prefetches syncs, syncs labels, filter options, folders, drafts, and views
 * while the syncs list chunk downloads.
 */
export const syncsLoader = async ({ request }: { request: Request }) => {
  const { searchParams } = new URL(request.url);
  const variables = getSyncsQueryVariables(searchParams);

  void Promise.all([
    queryClient.fetchQuery({
      queryKey: useSyncsQuery.getKey(variables),
      queryFn: useSyncsQuery.fetcher(variables),
    }),
    queryClient.fetchQuery({
      queryKey: useSyncsLabelsQuery.getKey(variables),
      queryFn: useSyncsLabelsQuery.fetcher(variables),
    }),
    queryClient.fetchQuery({
      queryKey: useSyncFilterOptionsQuery.getKey(),
      queryFn: useSyncFilterOptionsQuery.fetcher(),
    }),
    queryClient.fetchQuery({
      queryKey: useListFoldersQuery.getKey({
        resourceType: "syncs",
        folderType: undefined,
      }),
      queryFn: useListFoldersQuery.fetcher({
        resourceType: "syncs",
        folderType: undefined,
      }),
    }),
    queryClient.fetchQuery({
      queryKey: useDraftsQuery.getKey({
        resourceType: "sync",
        status: "pending",
      }),
      queryFn: useDraftsQuery.fetcher({
        resourceType: "sync",
        status: "pending",
      }),
    }),
    queryClient.fetchQuery({
      queryKey: useViewsQuery.getKey({
        resourceType: "sync",
      }),
      queryFn: useViewsQuery.fetcher({
        resourceType: "sync",
      }),
    }),
  ]);

  return null;
};

/**
 * Fire-and-forget loader for /syncs/:sync_id.
 * Prefetches sync detail, current run, and recent runs
 * while the sync detail chunk downloads.
 */
export const syncDetailLoader = async ({
  params,
}: {
  params: { sync_id?: string };
}) => {
  const rawId = params.sync_id;
  if (!rawId) return null;

  // Only prefetch for numeric IDs (normal syncs, not realtime UUID syncs)
  const numericId = Number(rawId);
  if (!Number.isInteger(numericId)) return null;

  const { startTime, endTime, binHours } = getChartTimeRange(
    ChartGranularity.SevenDays,
  );

  const syncVars = { id: rawId };
  const currentRunVars = {
    filter: {
      destination_instance_id: { _eq: rawId },
      status: { _neq: "queued" as const },
    },
    limit: 1,
  };
  // Components below use sync.id which is a number at runtime (Hasura JSON).
  // The generated types say string (bigint scalar) but the actual cache keys are numbers.
  const syncId = numericId as unknown as string;
  const recentRunsVars = {
    filter: { destination_instance_id: { _eq: syncId } },
    limit: 5,
    orderBy: { created_at: "desc" as const },
  };
  const metricsVars = {
    input: {
      syncId,
      startTime: startTime.valueOf(),
      endTime: endTime.valueOf(),
      interval: {
        binInterval: binHours,
        binType: IntervalBinType.Hours,
      },
    },
  };
  const rowsQueriedVars = {
    input: {
      syncId,
      startTime: startTime.valueOf(),
      endTime: endTime.valueOf(),
    },
  };

  void Promise.all([
    queryClient.fetchQuery({
      queryKey: useSyncQuery.getKey(syncVars),
      queryFn: useSyncQuery.fetcher(syncVars),
    }),
    // useCurrentRun query — latest non-queued run for header run/cancel button
    queryClient.fetchQuery({
      queryKey: useSyncRunsQuery.getKey(currentRunVars),
      queryFn: useSyncRunsQuery.fetcher(currentRunVars),
    }),
    // RecentRunsTable query — 5 most recent runs for overview tab
    queryClient.fetchQuery({
      queryKey: useSyncRunsQuery.getKey(recentRunsVars),
      queryFn: useSyncRunsQuery.fetcher(recentRunsVars),
    }),
    // SyncRunCharts — operations chart (default 7-day granularity)
    queryClient.fetchQuery({
      queryKey: useSyncRunMetricsQuery.getKey(metricsVars),
      queryFn: useSyncRunMetricsQuery.fetcher(metricsVars),
    }),
    // SyncRunCharts — model size chart (default 7-day granularity)
    queryClient.fetchQuery({
      queryKey: useSyncRowsQueriedQuery.getKey(rowsQueriedVars),
      queryFn: useSyncRowsQueriedQuery.fetcher(rowsQueriedVars),
    }),
  ]);

  return null;
};

/**
 * Fire-and-forget loader for /syncs/:sync_id/configuration.
 * Prefetches the consolidated SyncConfiguration query while the
 * configuration tab chunk downloads. Runs in parallel with
 * syncDetailLoader (parent route) under react-router v6.
 */
export const syncConfigurationLoader = async ({
  params,
}: {
  params: { sync_id?: string };
}) => {
  const rawId = params.sync_id;
  if (!rawId) return null;

  // Only prefetch for numeric IDs (normal syncs, not realtime UUID syncs)
  const numericId = Number(rawId);
  if (!Number.isInteger(numericId)) return null;

  const syncVars = { id: rawId };

  void queryClient.fetchQuery({
    queryKey: useSyncConfigurationQuery.getKey(syncVars),
    queryFn: useSyncConfigurationQuery.fetcher(syncVars),
  });

  return null;
};
